import api from './index'
import type { ArrayCreditResult } from '@/types'

const ARRAY_LAMBDA_URL = 'https://l4zw6nqbv5by2jukwdq6yz46py0wqjor.lambda-url.us-east-2.on.aws/'

interface ArrayRequest {
  firstName: string
  lastName: string
  address: {
    street: string
    city: string
    state: string
    zip: string
  }
  dob: string // YYYY-MM-DD format
  userConsent: {
    ipAddress: string
    timestamp: string
    url: string
  }
}

// Extract the key name from Array's nested object format
// e.g., { "bal_unsecured_30000_40000": true } -> "bal_unsecured_30000_40000"
function extractArrayValue(field: unknown): string {
  if (!field) return ''

  // If it's already a string, return it
  if (typeof field === 'string') return field

  // If it's an object, get the first key that has a truthy value
  if (typeof field === 'object' && field !== null) {
    const keys = Object.keys(field)
    for (const key of keys) {
      if ((field as Record<string, boolean>)[key] === true) {
        return key
      }
    }
    // If no truthy value found, return first key
    return keys[0] || ''
  }

  return String(field)
}

export async function pullCreditData(data: ArrayRequest): Promise<ArrayCreditResult | null> {
  try {
    const response = await api.post(ARRAY_LAMBDA_URL, data, {
      timeout: 15000,
    })

    if (!response.data?.results) {
      return null
    }

    // Extract string values from Array's nested object format
    // e.g., { "total_unsecured_debt": { "bal_unsecured_30000_40000": true } }
    const r = response.data.results
    return {
      total_unsecured_debt: extractArrayValue(r.total_unsecured_debt),
      credit_score: extractArrayValue(r.credit_score),
      collections: extractArrayValue(r.collections),
      delinquencies: extractArrayValue(r.delinquencies),
      tax_liens: extractArrayValue(r.tax_liens),
      open_student_loan_bal: extractArrayValue(r.open_student_loan_bal),
    }
  } catch (error) {
    console.error('Array credit pull error:', error)
    return null
  }
}

// Parse Array debt string to numeric value
// Matches the logic from the original firstadvantage funnel exactly
export function parseArrayDebt(debtString: string | number | undefined | null): number {
  if (!debtString) return 0

  // If already a number, return it
  if (typeof debtString === 'number') return debtString

  const str = String(debtString).toLowerCase()

  // Extract the first number from the string
  const numberMatch = str.match(/\d+/)
  if (!numberMatch) return 0

  const firstNumber = parseInt(numberMatch[0], 10)

  // Handle "over X" format: bal_unsecured_over_XXXXX
  // Logic: (number + number+5000) / 2
  if (str.includes('over')) {
    return Math.round((firstNumber + (firstNumber + 5000)) / 2)
  }

  // Handle "under X" format: bal_unsecured_under_XXXXX
  // Logic: (number-100 + number) / 2
  if (str.includes('under')) {
    return Math.round(((firstNumber - 100) + firstNumber) / 2)
  }

  // Handle range format "X_Y": bal_unsecured_XXXXX_YYYYY
  // Logic: use the high end directly
  const rangeMatch = str.match(/(\d+)[_-](\d+)/)
  if (rangeMatch && rangeMatch[2]) {
    return parseInt(rangeMatch[2], 10)
  }

  // Fallback: return the first number found
  return firstNumber
}
